import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { KEY_POINTS, LISTERS_DETAILS, CLAUSES, DECLARATIONS, FULL_PDF_URL } from './agreementContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1E2117';
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560}
        height={160}
        data-testid="signature-canvas"
        className="w-full border-2 border-dashed border-[#C6E76C] rounded-lg bg-[#FDFDF9] touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">Sign above using your mouse or finger</p>
        <button type="button" onClick={clear} className="text-xs font-semibold text-red-500 hover:underline" data-testid="signature-clear-button">
          Clear signature
        </button>
      </div>
    </div>
  );
};

export const SalesAgreementModal = ({ open, onOpenChange, application, seller, onSigned }) => {
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({ full_name: '', address: '', email: '', mobile: '', min_sale_price: '', id_type: '', id_number: '' });
  const [declarationsChecked, setDeclarationsChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [repName, setRepName] = useState('');
  const [repSignature, setRepSignature] = useState('');
  const [officeChecks, setOfficeChecks] = useState({ id_sighted: false, details_match: false, min_price_confirmed: false, fees_explained: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !application?.id) return;
    axios.get(`${API}/applications/${application.id}/agreement`, { withCredentials: true })
      .then(res => setExisting(res.data))
      .catch(() => setExisting(null));
    setForm({
      full_name: seller?.full_name || '',
      address: seller?.address || '',
      email: seller?.email || '',
      mobile: seller?.phone || '',
      min_sale_price: application?.nominated_sale_price || application?.minimum_acceptable_price || '',
      id_type: '',
      id_number: '',
      payid: '',
      bsb: '',
      account_number: ''
    });
    setSignature('');
    setRepName('');
    setRepSignature('');
    setOfficeChecks({ id_sighted: false, details_match: false, min_price_confirmed: false, fees_explained: false });
    setDeclarationsChecked(false);
  }, [open, application, seller]);

  const handleSign = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.address.trim() || !form.email.trim() || !form.mobile.trim()) {
      toast.error('Please complete all seller details');
      return;
    }
    if (!form.id_type || !form.id_number.trim()) {
      toast.error('Please select the ID type and enter the identification number');
      return;
    }
    if (!form.min_sale_price || parseFloat(form.min_sale_price) <= 0) {
      toast.error('Please set the Minimum Sale Price');
      return;
    }
    if (!form.payid.trim() && (!form.bsb.trim() || !form.account_number.trim())) {
      toast.error('Please provide a PayID or BSB + Account Number for your payout');
      return;
    }
    if (!declarationsChecked) {
      toast.error('Please confirm the seller declarations');
      return;
    }
    if (!signature) {
      toast.error('The seller must sign the agreement in the signature box');
      return;
    }
    if (!repName.trim() || !repSignature) {
      toast.error('A Listers representative must enter their name and sign');
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(`${API}/applications/${application.id}/agreement`, {
        seller_full_name: form.full_name.trim(),
        seller_address: form.address.trim(),
        seller_email: form.email.trim(),
        seller_mobile: form.mobile.trim(),
        min_sale_price: parseFloat(form.min_sale_price) || null,
        id_type: form.id_type,
        id_number: form.id_number.trim(),
        payid: form.payid.trim() || null,
        bsb: form.bsb.trim() || null,
        account_number: form.account_number.trim() || null,
        signature_data: signature,
        rep_name: repName.trim(),
        rep_signature_data: repSignature,
        office_checks: officeChecks
      }, { withCredentials: true });
      toast.success('Sales agreement signed and saved');
      setExisting(res.data);
      if (onSigned) onSigned();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(`Missing/invalid: ${detail.map(d => d.loc?.slice(-1)[0]).filter(Boolean).join(', ')}`);
      } else if (error.response) {
        toast.error(`Failed to save agreement (server error ${error.response.status}) — please try again`);
      } else {
        toast.error('Failed to save agreement — network error, please check your connection and try again');
      }
    } finally {
      setSaving(false);
    }
  };

  const agreementDate = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sales-agreement-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold text-[#1E2117]">
            Seller Sales &amp; Consignment Agreement
          </DialogTitle>
        </DialogHeader>

        <div className="text-center border-b-4 border-[#C6E76C] pb-4">
          <img src="/listers-logo.png" alt="Listers" className="h-12 w-auto mx-auto mb-3" data-testid="agreement-logo" />
          <p className="text-sm font-bold tracking-[0.2em] text-[#1E2117]">LISTERS | SELL MORE. STRESS LESS.</p>
          <p className="text-xs text-gray-500 mt-1">We store it. We list it. We sell it. You get paid.</p>
          <p className="text-xs text-gray-500 mt-1">
            Agreement reference: <span className="font-semibold">{application?.reference_code}</span> · Agreement date: <span className="font-semibold">{existing?.signed_at ? new Date(existing.signed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : agreementDate}</span>
          </p>
          <p className="text-xs text-gray-400 mt-0.5">Version 1.3 – July 2026 · <a href={FULL_PDF_URL} target="_blank" rel="noreferrer" className="underline hover:text-[#5A7014]" data-testid="agreement-pdf-link">Download full PDF (incl. all schedules)</a></p>
        </div>

        {/* Key Points */}
        <div className="bg-[#F7F9F2] rounded-lg p-4 border border-[#E2E4E0]">
          <h3 className="text-base font-bold text-[#1E2117] mb-2">One-Page Key Points Summary</h3>
          <p className="text-[11px] text-gray-500 mb-3">Plain-English overview only. If this summary differs from the operative clauses or a completed schedule, the clauses and schedule prevail.</p>
          <div className="space-y-3">
            {KEY_POINTS.map(kp => (
              <div key={kp.title}>
                <p className="text-sm font-bold text-[#3D4237]">{kp.title}</p>
                <ul className="list-disc pl-5 space-y-1 mt-1">
                  {kp.points.map((p) => <li key={p} className="text-xs text-gray-600 leading-relaxed">{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Agreement Details */}
        <div className="rounded-lg border border-[#E2E4E0] overflow-hidden">
          <div className="bg-[#1E2117] px-4 py-2"><h3 className="text-sm font-bold text-[#C6E76C]">Agreement Details</h3></div>
          <div className="p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-gray-700">
            <p><span className="font-semibold">Listers legal entity:</span> {LISTERS_DETAILS.entity}</p>
            <p><span className="font-semibold">Business premises:</span> {LISTERS_DETAILS.address}</p>
            <p><span className="font-semibold">Storage premises:</span> {LISTERS_DETAILS.storage}</p>
            <p><span className="font-semibold">Licensing status:</span> {LISTERS_DETAILS.licensing}</p>
            <p><span className="font-semibold">Contact:</span> {LISTERS_DETAILS.contact}</p>
            <p><span className="font-semibold">Item:</span> {application?.item_title}</p>
          </div>
        </div>

        {/* Seller details form (Schedule 1) */}
        <div className="rounded-lg border border-[#E2E4E0] overflow-hidden">
          <div className="bg-[#1E2117] px-4 py-2"><h3 className="text-sm font-bold text-[#C6E76C]">Schedule 1 — Seller Details</h3></div>
          <div className="p-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Full Legal Name *</label>
              <input type="text" value={form.full_name} disabled={!!existing} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-name-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Residential Address *</label>
              <input type="text" value={form.address} disabled={!!existing} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-address-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Email *</label>
              <input type="email" value={form.email} disabled={!!existing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-email-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Mobile *</label>
              <input type="tel" value={form.mobile} disabled={!!existing} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-mobile-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Minimum Sale Price ($) *</label>
              <input type="number" value={form.min_sale_price} disabled={!!existing} onChange={(e) => setForm({ ...form, min_sale_price: e.target.value })} className="w-full h-10 px-3 border-2 border-[#C6E76C] rounded-md text-sm font-bold disabled:bg-gray-50" data-testid="agreement-price-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">ID Type * <span className="normal-case font-normal text-gray-400">(must be sighted by Listers)</span></label>
              <div className="grid grid-cols-2 gap-2" data-testid="agreement-id-type-options">
                {['Drivers License', 'Passport', 'Proof of Age Card', 'Other Photo Government ID'].map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer ${form.id_type === opt ? 'border-[#C6E76C] bg-[#F7FBEC] font-semibold' : 'border-[#E2E4E0]'} ${existing ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={form.id_type === opt}
                      disabled={!!existing}
                      onChange={() => setForm({ ...form, id_type: form.id_type === opt ? '' : opt })}
                      className="w-4 h-4 accent-[#C6E76C]"
                      data-testid={`agreement-id-type-${opt.toLowerCase().replace(/ /g, '-')}`}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Identification Number *</label>
              <input type="text" value={form.id_number} disabled={!!existing} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-id-number-input" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">PayID (email or mobile)</label>
              <input type="text" value={form.payid} disabled={!!existing} onChange={(e) => setForm({ ...form, payid: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" placeholder="Or provide BSB + Account below" data-testid="agreement-payid-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">BSB</label>
                <input type="text" value={form.bsb} disabled={!!existing} onChange={(e) => setForm({ ...form, bsb: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" placeholder="000-000" data-testid="agreement-bsb-input" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Account Number</label>
                <input type="text" value={form.account_number} disabled={!!existing} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm disabled:bg-gray-50" data-testid="agreement-account-input" />
              </div>
            </div>
          </div>
          {/* Prominent Minimum Sale Price callout */}
          <div className="mx-4 mb-4 rounded-lg border-2 border-[#C6E76C] bg-[#F7F9F2] p-3 flex items-center justify-between" data-testid="agreement-min-price-callout">
            <div>
              <p className="text-sm font-bold text-[#1E2117]">Minimum Sale Price set by the Seller</p>
              <p className="text-[11px] text-gray-500">Listers cannot complete a sale below this amount without the Seller's written approval (clause 3.3).</p>
            </div>
            <p className="text-2xl font-black text-[#1E2117] whitespace-nowrap pl-4">${form.min_sale_price ? parseFloat(form.min_sale_price).toLocaleString() : '—'}</p>
          </div>
        </div>

        {/* Full terms */}
        <div className="rounded-lg border border-[#E2E4E0] overflow-hidden">
          <div className="bg-[#1E2117] px-4 py-2"><h3 className="text-sm font-bold text-[#C6E76C]">Agreement Terms</h3></div>
          <div className="max-h-72 overflow-y-auto p-4 space-y-4" data-testid="agreement-terms-scroll">
            {CLAUSES.map(c => (
              <div key={c.title}>
                <p className="text-sm font-bold text-[#1E2117]">{c.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line mt-1">{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        {existing ? (
          <div className="rounded-lg border-2 border-[#C6E76C] bg-[#F7F9F2] p-4" data-testid="agreement-signed-block">
            <p className="text-sm font-bold text-[#1E2117] mb-2">✓ Agreement signed and binding</p>
            <p className="text-xs text-gray-600">Signed by <span className="font-semibold">{existing.seller_full_name}</span> on {new Date(existing.signed_at).toLocaleString('en-AU', { dateStyle: 'long', timeStyle: 'short' })}</p>
            {existing.id_type && <p className="text-xs text-gray-600 mt-1">ID verified: <span className="font-semibold">{existing.id_type}</span> — {existing.id_number}</p>}
            {existing.min_sale_price && <p className="text-xs text-gray-600 mt-1">Minimum Sale Price: <span className="font-semibold">${Number(existing.min_sale_price).toLocaleString()}</span></p>}
            {(existing.payid || existing.bsb) && <p className="text-xs text-gray-600 mt-1">Payout details: <span className="font-semibold">{existing.payid ? `PayID ${existing.payid}` : `BSB ${existing.bsb} / Acc ${existing.account_number}`}</span></p>}
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D4237]">Seller signature</p>
                {existing.signature_data && <img src={existing.signature_data} alt="Seller signature" className="mt-1 h-20 bg-white border border-[#E2E4E0] rounded" />}
              </div>
              {existing.rep_signature_data && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D4237]">Listers representative — {existing.rep_name}</p>
                  <img src={existing.rep_signature_data} alt="Representative signature" className="mt-1 h-20 bg-white border border-[#E2E4E0] rounded" />
                </div>
              )}
            </div>
            {existing.office_checks && (
              <div className="mt-3 pt-3 border-t border-[#E2E4E0]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Office use only</p>
                {[
                  { key: 'id_sighted', label: "Seller's photo ID sighted" },
                  { key: 'details_match', label: "Seller's details match ID" },
                  { key: 'min_price_confirmed', label: 'Minimum sale price confirmed' },
                  { key: 'fees_explained', label: 'Commission and withdrawal fee explained' }
                ].map(c => (
                  <p key={c.key} className="text-xs text-gray-600">{existing.office_checks[c.key] ? '☑' : '☐'} {c.label}</p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSign} className="space-y-4">
            <div className="rounded-lg border border-[#E2E4E0] p-4">
              <h3 className="text-sm font-bold text-[#1E2117] mb-2">Execution and Seller Declarations</h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={declarationsChecked} onChange={(e) => setDeclarationsChecked(e.target.checked)} className="mt-1" data-testid="agreement-declarations-checkbox" />
                <span className="text-xs text-gray-600">By signing, I confirm that:
                  <ul className="list-disc pl-4 mt-1 space-y-0.5">
                    {DECLARATIONS.map((d) => <li key={d}>{d}</li>)}
                  </ul>
                </span>
              </label>
            </div>

            <div className="rounded-lg border border-[#E2E4E0] p-4">
              <h3 className="text-sm font-bold text-[#1E2117] mb-2">Seller Signature</h3>
              <SignaturePad onChange={setSignature} />
            </div>

            <div className="rounded-lg border border-[#E2E4E0] p-4">
              <h3 className="text-sm font-bold text-[#1E2117] mb-2">Listers Representative</h3>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Representative Name *</label>
              <input type="text" value={repName} onChange={(e) => setRepName(e.target.value)} className="w-full h-10 px-3 border border-[#E2E4E0] rounded-md text-sm mb-3" data-testid="agreement-rep-name-input" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D4237] mb-1">Representative Signature *</p>
              <SignaturePad onChange={setRepSignature} />
            </div>

            <div className="rounded-lg border-2 border-dashed border-[#9CA3AF] bg-gray-50 p-4" data-testid="agreement-office-use-section">
              <h3 className="text-sm font-bold text-[#1E2117] mb-1">Office Use Only</h3>
              <p className="text-[11px] text-gray-500 mb-2">To be completed by the Listers representative.</p>
              <div className="space-y-2">
                {[
                  { key: 'id_sighted', label: "Seller's photo ID sighted" },
                  { key: 'details_match', label: "Seller's details match ID" },
                  { key: 'min_price_confirmed', label: 'Minimum sale price confirmed' },
                  { key: 'fees_explained', label: 'Commission and withdrawal fee explained' }
                ].map(c => (
                  <label key={c.key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={officeChecks[c.key]}
                      onChange={(e) => setOfficeChecks({ ...officeChecks, [c.key]: e.target.checked })}
                      data-testid={`office-check-${c.key}`}
                    />
                    <span className="text-xs text-gray-700">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full h-12 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-bold text-base" data-testid="agreement-sign-button">
              {saving ? 'Saving...' : 'Sign Agreement & Make It Binding'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
